import {
  BrandPurple,
  color,
  css,
  GlitterGray,
  Heading,
  Modal,
  Paragraph,
  Radio,
  Stack,
} from 'canoe';
import Papa from 'papaparse';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import { LogicRulesNames } from '@candu-ai/segments/types';
import { FormikFieldSetter } from 'craft/types';
import { useHandleSegementSetUpload } from 'dashboard/pages/Segment/components/Rule/SetContainsRule/useHandleSegementSetUpload';

interface DraggableAreaProps {
  isDragActive: boolean;
}

const CHUNK_SIZE = 1024 * 64; // 64kb

const getBorderColor = ({ isDragActive }: DraggableAreaProps) => {
  if (!isDragActive) {
    return color(GlitterGray);
  }

  return color(BrandPurple);
};

const DraggableArea = styled.div<DraggableAreaProps>`
  align-items: center;
  border: 1px dashed ${getBorderColor};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 16px;
  overflow: hidden;
  padding: 70px 20px;
  position: relative;
  width: 100%;
  cursor: pointer;

  p {
    margin-bottom: 0;
  }
`;

const DragableInput = styled.input`
  bottom: 0;
  cursor: pointer;
  left: 0;
  outline: none;
  position: absolute;
  right: 0;
  top: 0;
`;

const ErrorText = styled.span`
  ${({ theme }) => css`
    color: ${theme.colors.ParadiseRed};
    height: 2em;
  `}
`;

const Filename = styled.span`
  font-size: 14px;
  margin-bottom: 16px;
`;

interface UploadState {
  file: File;
  headerRow: string[];
}

interface ContainsRuleUploadDialogProps {
  base: string;
  setFieldValue: FormikFieldSetter;
  toggle: () => void;
  operationType?: LogicRulesNames;
}

export const SetContainsRuleUploadDialog = ({
  base,
  setFieldValue,
  toggle,
  operationType = LogicRulesNames.Contains,
}: ContainsRuleUploadDialogProps) => {
  const [uploadError, setUploadError] = useState<string>();
  const [uploadedFile, setUploadedFile] = useState<UploadState>();
  const [column, setColumn] = useState<string>();
  const { setId, onChunk } = useHandleSegementSetUpload();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'text/csv',
    onDrop: (acceptedFiles) => {
      setUploadError(undefined);
      if (!acceptedFiles[0]) {
        return;
      }
      Papa.parse(acceptedFiles[0], {
        preview: 1,
        complete: (results, file) => {
          if (!results.data[0]) {
            setUploadError('Please double check the file supplied is a CSV with at least one row');
            return;
          }

          // @ts-ignore
          setColumn(results.data[0][0] as string);
          setUploadedFile({
            headerRow: results.data[0] as string[],
            file,
          });
        },
      });
    },
    onDropRejected: (rejectedFiles) => {
      if (!rejectedFiles[0].errors[0].code) {
        return;
      }

      const error = (() => {
        switch (rejectedFiles[0].errors[0].code) {
          case 'too-many-files':
            return 'Only one file is supported';
          case 'file-too-large':
            return 'File is too large';
          case 'file-invalid-type':
          default:
            return 'The selected file must be of text/csv file format';
        }
      })();
      setUploadError(error);
    },
  });

  const onClose = () => {
    toggle();
  };

  const handleSave = () => {
    if (!uploadedFile || !column) {
      return;
    }

    // @ts-ignore
    Papa.parse(uploadedFile.file, {
      header: true,
      chunkSize: CHUNK_SIZE,
      chunk: (results) => {
        onChunk(
          results.data
            .map((row: Record<string, string>) => row[column])
            .filter((id) => id !== undefined),
        );
      },
      complete() {
        setFieldValue(`${base}.operation`, { setId, type: operationType });
        toggle();
      },
    });
  };

  const modalTitle = !uploadedFile ? 'Bulk upload values via CSV' : 'Select a column';

  return (
    <Modal onClose={onClose} closeOnClickOutside data-cy="csv-upload-modal">
      <Modal.Header title={modalTitle} />
      <Modal.Body>
        {!uploadedFile && (
          <Stack justify="center">
            {/* @ts-ignore */}
            <DraggableArea {...getRootProps()} isDragActive={isDragActive}>
              <Heading size="M">Drag & Drop</Heading>
              <p>
                {isDragActive ? 'Drop' : 'Drag'} a CSV into this area, or click here to upload a
                file.
              </p>
              {/* @ts-ignore */}
              <DragableInput {...getInputProps()} type="file" accept="text/csv" />
            </DraggableArea>
            <ErrorText>{uploadError}</ErrorText>
          </Stack>
        )}
        {uploadedFile && (
          <Stack>
            <Heading size="XXS">Group users together based on a User ID or Trait Value.</Heading>
            <Heading size="XXS">File:</Heading>
            <Filename>
              {uploadedFile.file.name} ({(uploadedFile.file.size / 1024).toFixed(0)}kb)
            </Filename>
            <Heading size="XXS">Column:</Heading>
            <Radio
              items={uploadedFile.headerRow.map((name) => ({ label: name, value: name }))}
              direction="vertical"
              selected={column}
              onChange={(value) => setColumn(value as string)}
            />
            <Paragraph>
              This selection will replace any prior CSV upload for this Segment rule.
            </Paragraph>
          </Stack>
        )}
      </Modal.Body>
      <Modal.Footer
        secondaryAction={{
          children: 'Cancel',
          onClick: onClose,
          'data-cy': 'cancel-upload',
        }}
        primaryAction={
          uploadedFile
            ? {
                children: 'Save',
                variant: 'secondary',
                onClick: handleSave,
                'data-cy': 'upload',
              }
            : undefined
        }
      />
    </Modal>
  );
};
